import { useRef } from 'react'

import ActivityWrapper from '~/bootcamp/Activities/components/ActivityWrapper'
import Divider from '~/bootcamp/Activities/components/Divider'
import SelectionButton from '~/bootcamp/Activities/components/SelectionButton'
import FractionsActivityWrapper from '~/bootcamp/Activities/Fractions/components/FractionsActivityWrapper'
import useFocus from '~/bootcamp/Activities/hooks/useFocus'
import { ActivityProps } from '~/bootcamp/Activities/types/activities.types'
import { useEmitInstance } from '~/bootcamp/Activities/utils/instance.utils'

import useFractionMixedToImproperChoose from './useFractionMixedToImproperChoose'

interface FractionPropsModel {
  numerator?: number
  denominator?: number
  dividerClassName?: string
  quotient?: number
}

function Fraction({ numerator, denominator, quotient, dividerClassName = '' }: FractionPropsModel): React.ReactElement {
  return (
    <div className="flex items-center space-x-1">
      {!!quotient && <span className="text-2xl">{quotient}</span>}
      <div className="flex flex-col items-center">
        <span className="text-2xl">{numerator}</span>
        <Divider className={dividerClassName} />
        <span className="text-2xl">{denominator}</span>
      </div>
    </div>
  )
}

function FractionMixedToImproperChoose({
  answerState,
  actionButtonsFC,
  difficultyLevel,
  autofocus = true,
  onAnswerStateChange,
  onNextQuestionClick,
  onInstanceChange,
}: ActivityProps): React.ReactElement {
  const { question, options, formValid, selectedAnswer, onInputChange, handleCheckAnswer, goToNextQuestion } =
    useFractionMixedToImproperChoose({
      difficultyLevel,
      onAnswerStateChange,
      onNextQuestionClick,
    })

  const inputRef = useRef<HTMLInputElement>(null)
  useFocus(autofocus, answerState, inputRef)

  useEmitInstance(
    onInstanceChange,
    question && options?.length
      ? `${question.quotient ? `${question.quotient} ` : ''}\\frac{${question.numerator}}{${question.denominator}}; ` +
          `options: ${options
            .map(
              ({ quotient, numerator, denominator }) =>
                `${quotient ? `${quotient} ` : ''}\\frac{${numerator}}{${denominator}}`,
            )
            .join(', ')}`
      : null,
  )

  return (
    <ActivityWrapper>
      <FractionsActivityWrapper>
        <Fraction denominator={question?.denominator} numerator={question?.numerator} quotient={question?.quotient} />
      </FractionsActivityWrapper>
      <div className="flex justify-around space-x-2 sm:space-x-8 w-full">
        {options &&
          options.map(({ quotient, numerator, denominator }, index) => (
            <SelectionButton
              className="p-2 flex-1 flex justify-center"
              isSelected={selectedAnswer === index}
              isWrong={selectedAnswer === index && answerState === false}
              key={`${quotient}-${numerator}-${denominator}`}
              onClick={onInputChange.bind(null, index)}
            >
              <Fraction
                denominator={denominator}
                dividerClassName={selectedAnswer === index ? 'bg-white' : ''}
                numerator={numerator}
              />
            </SelectionButton>
          ))}
      </div>
      {actionButtonsFC &&
        actionButtonsFC({
          formValid,
          correctAnswer: answerState ?? false,
          submitAnswerAction: handleCheckAnswer,
          clearFormAction: () => onInputChange(),
          goToNextQuestionAction: goToNextQuestion,
        })}
    </ActivityWrapper>
  )
}

export default FractionMixedToImproperChoose
